import { useEffect, useMemo, useState } from "react"
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api"
import { MapPin } from "lucide-react"
import { GOOGLE_MAPS_API_KEY } from "../../../../api/constants"
import { adressType } from "../../../../data/Types"
import { returnAddressString } from "../../../../services/functions"

type ObjectLocationMapType = {
    adress?: adressType
    showLabel?: boolean
    classProps?: string
    heightClass?: string
}

type LatLng = { lat: number; lng: number }

const mapContainerStyle = { width: "100%", height: "100%" }
const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: "cooperative",
}

const positionFromAdress = (adress?: adressType): LatLng | undefined => {
    const lat = adress?.coordinates?.latitude
    const lng = adress?.coordinates?.longitude
    if (typeof lat !== "number" || typeof lng !== "number" || (lat === 0 && lng === 0)) return undefined
    return { lat, lng }
}

export default function ObjectLocationMap(props: ObjectLocationMapType) {
    const showLabel = props.showLabel ?? true
    const addressLabel = useMemo(() => (props.adress ? returnAddressString(props.adress) : ""), [props.adress])
    const storedPosition = useMemo(() => positionFromAdress(props.adress), [props.adress])
    const [geocodedPosition, setGeocodedPosition] = useState<LatLng>()
    const [status, setStatus] = useState<"idle" | "loading" | "missing" | "error">("idle")
    const [mapsReady, setMapsReady] = useState(false)

    const hasAddress = Boolean(props.adress?.street && props.adress?.city)
    const position = storedPosition ?? geocodedPosition

    useEffect(() => {
        setGeocodedPosition(undefined)
        if (!hasAddress) {
            setStatus("missing")
            return
        }
        if (storedPosition) {
            setStatus("idle")
            return
        }
        if (!mapsReady || !GOOGLE_MAPS_API_KEY) {
            setStatus(GOOGLE_MAPS_API_KEY ? "loading" : "error")
            return
        }

        let cancelled = false
        setStatus("loading")
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address: addressLabel, region: "DE" }, (results, geocodeStatus) => {
            if (cancelled) return
            if (geocodeStatus === "OK" && results?.[0]?.geometry?.location) {
                setGeocodedPosition({
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                })
                setStatus("idle")
                return
            }
            setStatus("error")
        })
        return () => {
            cancelled = true
        }
    }, [addressLabel, hasAddress, mapsReady, storedPosition])

    const placeholder = (text: string) => (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-base-content/70">
            <MapPin className="h-6 w-6" />
            <p>{text}</p>
        </div>
    )

    const message = !GOOGLE_MAPS_API_KEY
        ? "Kartenansicht ist nicht konfiguriert."
        : !hasAddress || status === "missing"
          ? "Für dieses Objekt ist keine vollständige Adresse hinterlegt."
          : status === "error"
            ? "Der Standort konnte anhand der Adresse nicht ermittelt werden."
            : !position
              ? "Karte wird geladen…"
              : undefined

    return (
        <div className={`flex flex-col ${showLabel ? "pt-4" : "h-full"} ${props.classProps ?? ""}`}>
            {showLabel && <label className="text-sm pl-2 pb-1">Standort</label>}
            <div
                className={`w-full overflow-hidden rounded-default border border-base-200 bg-base-100 ${
                    props.heightClass ?? (showLabel ? "h-56" : "h-full min-h-[14rem]")
                }`}>
                {GOOGLE_MAPS_API_KEY ? (
                    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} onLoad={() => setMapsReady(true)}>
                        {position && !message ? (
                            <GoogleMap mapContainerStyle={mapContainerStyle} center={position} zoom={16} options={mapOptions}>
                                <Marker position={position} title={addressLabel} />
                            </GoogleMap>
                        ) : (
                            placeholder(message ?? "Karte wird geladen…")
                        )}
                    </LoadScript>
                ) : (
                    placeholder(message ?? "Kartenansicht ist nicht konfiguriert.")
                )}
            </div>
        </div>
    )
}
